import { useEffect, useState } from "react";

export interface PreviewPageNavProps {
  /** 1-based current page, driven by the viewer's scroll tracking. */
  current: number;
  /** Total rendered pages. */
  total: number;
  /** Jump to a 1-based page number (clamped by the viewer). */
  onJump: (page: number) => void;
}

/**
 * Sticky bottom page navigator shared by PdfViewer and DocxPreview: shows
 * "第 [输入框] / N 页" and jumps on Enter/blur. `current` is kept in sync with
 * the viewer's scroll position; the input lets the user type a page directly.
 */
export function PreviewPageNav({ current, total, onJump }: PreviewPageNavProps) {
  const [value, setValue] = useState(String(current));

  // Re-sync when the viewer reports a new current page (from scrolling).
  useEffect(() => {
    setValue(String(current));
  }, [current]);

  if (total <= 0) return null;

  const commit = () => {
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) {
      setValue(String(current));
      return;
    }
    onJump(n);
  };

  return (
    <div
      data-testid="preview-page-nav"
      style={{
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "6px 12px",
        background: "var(--jerry-gray-50, #ffffff)",
        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
        color: "var(--jerry-text-secondary)",
        fontSize: 12,
        userSelect: "none",
      }}
    >
      <span>第</span>
      <input
        data-testid="preview-page-input"
        type="text"
        inputMode="numeric"
        aria-label="跳转到页码"
        value={value}
        size={Math.max(2, String(total).length)}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            (e.target as HTMLInputElement).blur();
          }
        }}
        onBlur={commit}
        style={{
          width: `${Math.max(2, String(total).length) + 2}ch`,
          textAlign: "center",
          padding: "2px 4px",
          fontSize: 12,
          border: "1px solid rgba(0, 0, 0, 0.15)",
          borderRadius: 4,
          background: "#fff",
          color: "var(--jerry-text-primary, #1A1F27)",
        }}
      />
      <span>
        / {total} 页
      </span>
    </div>
  );
}
